import { useEffect, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import clsx from 'clsx'
import { useFormik } from 'formik'
import Dropdown from '../../../../sharedComponent/Dropdown/Dropdown'
import CustomerInfo from './CustomerInfo'
import AgentInfo from './AgentInfo'
import RecordingInfo from './RecordingInfo'
import {
  GETCLIENTFEATUREFLAGS,
  GET_AGENT_INFO_BY_TPV_ID,
  GET_CLIENT_ALLOWED_COMPONENTS,
  GET_CUSTOMER_INFO_BY_TPV_ID,
  GET_TPV_RECORDING,
  UPDATE_TPV,
} from '../../../../../utils/GraphQLAPI'
import { ScriptPromptComponent } from '../ScriptPromptComponent'
import {
  alert,
  capitalizeFirstLetter,
  getToken,
  statusBadgeClassName,
} from '../../../../../utils/helper'
import { AnyCnameRecord } from 'dns'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import backIcon from '../../../../../_metronic/images/back_icon.svg'
import './CustomerDetails.css'
import { createBrowserHistory } from 'history'
import ContractTPVReports from './ContractTPVReports'
import { GLOBAL_CONSTANTS } from '../../../../../utils/GlobalConstants'
import ContactIntelligence from "./ContactIntelligence"
import fileDownload from 'js-file-download'

type LocationState = {
  tpvDetails: AnyCnameRecord
  filterOptions: any
}

const defaultValues = {
  qcStatus: '',
}

const statusOptionList = [
  { id: 'ACCEPTED', value: 'Accepted', isDisabled: false },
  { id: 'REJECTED', value: 'Rejected', isDisabled: false },
  { id: 'PENDING', value: 'Pending', isDisabled: false },
  { id: 'AUTO_APPROVED', value: 'Auto_Approved', isDisabled: true },
]

const solarStatusOptionList = [
  { id: 'ACCEPTED', value: 'Accepted', isDisabled: false },
  { id: 'REJECTED', value: 'Rejected', isDisabled: false },
  { id: 'PENDING', value: 'Pending', isDisabled: false },
  { id: 'NEED_MORE_INFORMATION', value: 'Need More Information', isDisabled: false },
  { id: 'AUTO_APPROVED', value: 'Auto_Approved', isDisabled: true },
]

const MySwal = withReactContent(Swal)
const fetchPolicy = GLOBAL_CONSTANTS.networkOnly

// Customer Details Header component
export const CustomerDetailsHeader = ({
  tab,
  setTab,
  formik,
  tpvDetails,
  featureFlags,
  customerData,
  isSolarProduct,
}: any) => {
  const getUserParams = useParams()
  const UserId = JSON.parse(window.localStorage.getItem('userId') ?? '')
  const [isDownloading, setIsDownloading] = useState(false) // add state for spinner
  const [updateTpv] = useMutation(UPDATE_TPV, {
    fetchPolicy: GLOBAL_CONSTANTS.networkOnlyMutation,
    onCompleted: (data: any) => {
      const options: any = {
        customClass: {
          popup: 'swal2-border-radius',
        },
        title: 'Status updated successfully',
        icon: 'success',
        confirmButtonColor: '#3E97FF',
      }
      MySwal.fire(options)
    },
    onError: (err) => alert(err.message, 'error'),
  })
  const handleSelectedItem = (e: any, name: any) => {
    formik.setFieldValue(name, e.label)
    formik.setFieldValue(`${name}Id`, e.value)
    updateTpv({
      variables: {
        updateTpv: {
          tpvId: tpvDetails.tpvId,
          qcStatus: e.value,
          disposition: tpvDetails.disposition,
          qcMemberId: UserId,
        },
      },
    })
  }

  const handleDownloadPdf = async (tpvId: string, enrollmentPosition?: number) => {
    setIsDownloading(true)

    try {
      const downloadURL = `${process.env.REACT_APP_BASE_URL}customer/pdf/${tpvId}/${enrollmentPosition}`
      const TOKEN = await getToken()
      const res = await fetch(downloadURL, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${TOKEN}`,
          // responseType: 'arraybuffer'
          contentType: 'application/pdf',
        }),
      })
      const blob = await res.blob()
      const newBlob = new Blob([blob])
      fileDownload(newBlob, `${customerData?.firstName}_${customerData?.lastName}_tpvreport.pdf`)
    } catch (err) {
      alert('Failed to download PDF', 'error')
    } finally {
      setIsDownloading(false)
    }
  }
  return (
    <>
      <div className='tab d-flex flex-row justify-content-between'>
        <ul
          className='nav nav-stretch nav-line-tabs fw-bold border-transparent flex-nowrap'
          role='tablist'
        >
          <li className='nav-item'>
            <div
              className={clsx(`nav-link cursor-pointer text-gray-600 fs-4`, { active: tab === 0 })}
              onClick={() => setTab(0)}
              role='tablist'
            >
              Customer Info
            </div>
          </li>
          <ul className="nav">
            {featureFlags?.getClientFeatureFlags?.enableLineTypeIntelligence && (
              <li className='nav-item'>
                <div
                  className={clsx('nav-link cursor-pointer text-gray-600 fs-4', {
                    active: tab === 1,
                  })}
                  onClick={() => setTab(1)}
                  role='tab'
                >
                  Contact Intelligence
                </div>
              </li>
            )}
          </ul>
          <li className='nav-item'>
            <div
              className={clsx(`nav-link cursor-pointer text-gray-600 fs-4`, { active: tab === 2 })}
              onClick={() => setTab(2)}
              role='tab'
            >
              Agent Info
            </div>
          </li>
          <li className='nav-item'>
            <div
              className={clsx(`nav-link cursor-pointer text-gray-600 fs-4`, { active: tab === 3 })}
              onClick={() => setTab(3)}
              role='tab'
            >
              Recording Info
            </div>
          </li>
          {(featureFlags?.getClientFeatureFlags?.enableEmailDocumentVerification ||
            featureFlags?.getClientFeatureFlags?.enableSmsDocumentVerification) && (
              <li className='nav-item'>
                <div
                  className={clsx(`nav-link cursor-pointer text-gray-600 fs-4`, { active: tab === 4 })}
                  onClick={() => setTab(4)}
                  role='tab'
                >
                  Contract Info
                </div>
              </li>
            )}
        </ul>
        <div className='d-flex align-items-center'>
          {getUserParams?.adminUser !== 'qcuser' && (
            <div className='fs-6 fw-semibold lh-23px me-2'>
              <button
                className='btn btn-primary btn-sm d-flex align-items-center'
                onClick={() => handleDownloadPdf(tpvDetails?.tpvId, tpvDetails?.enrollmentPosition)}
                disabled={
                  tpvDetails?.tpvStatus === 'PENDING' ||
                  tpvDetails?.tpvStatus === 'UNVERIFIED' ||
                  isDownloading // disable while downloading
                }
              >
                {isDownloading && (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                )}
                Download as a PDF
              </button>
            </div>
          )}
          <div className='fs-6 fw-semibold lh-23px'>
            TPV :
            <span className={`${statusBadgeClassName(tpvDetails?.tpvStatus)} fs-7`}>
              {capitalizeFirstLetter(tpvDetails?.tpvStatus === 'NO_TPV' ? 'No Tpv' : tpvDetails?.tpvStatus)}
            </span>
          </div>
          <div className='fs-6 fw-semibold lh-23px pl-25'>
            EDI :{' '}
            {Boolean(tpvDetails?.ediStatus) ? (
              <span className='badge badge-light-success fs-7'>{tpvDetails?.ediStatus}</span>
            ) : (
              'N/A'
            )}
          </div>
          <div className='fs-6 fw-semibold lh-23px pl-25 d-flex align-items-center qc-stat'>
            QC :
            <Dropdown
              fieldName='qcStatus'
              optionList={isSolarProduct ? solarStatusOptionList : statusOptionList}
              handleSelectedItem={handleSelectedItem}
              formik={formik}
            />
          </div>
        </div>
      </div>
    </>
  )
}

// Customer Details Data Component
const CustomerDetails = (props: any) => {
  const navigate = useNavigate()
  const location = useLocation()
  const history = createBrowserHistory()
  const tpvDetails: any = (location.state as LocationState)?.tpvDetails
  const filterOption: any = (location.state as LocationState)?.filterOptions
  const getUrlParams = useParams()
  const [tab, setTab] = useState<number>(0)
  const [customerData, setCustomerData] = useState<any>(null)
  const [agentData, setAgentData] = useState<any>(null)
  const [recordingData, setRecordingData] = useState<any>(null)
  const [welcomeMessageRecordingData, setWelcomeMessageRecordingData] = useState<any>(null)
  const [recordingTab, setRecordingTab] = useState<any>(0)
  const [urlRedirect, setUrlRedirect] = useState('')
  const [isSolarProduct, setIsSolarProduct] = useState<any>(false)

  useEffect(() => {
    if (getUrlParams.adminUser === 'systemadmin') {
      setUrlRedirect('tpv-report')
    } else if (getUrlParams.adminUser === 'qcuser') {
      setUrlRedirect('tpv-recording-list')
    }
  }, [getUrlParams])
  useEffect(() => {
    !tpvDetails &&
      getUrlParams.adminUser === 'systemadmin' &&
      navigate(`/${getUrlParams.adminUser}/tpv-report`)
    !tpvDetails &&
      getUrlParams.adminUser === 'qcuser' &&
      navigate(`/${getUrlParams.adminUser}/tpv-recording-list`)
  }, [])// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    history.listen(({ action }) => {
      if (action === 'POP') {
        handleBackNavigation()
      }
    })
  }, [])// eslint-disable-line react-hooks/exhaustive-deps

  const handleBackNavigation = () => {
    navigate(`/${getUrlParams.adminUser}/${urlRedirect}`, {
      state: {
        filterOptions: filterOption,
      },
    })
  }
  const formik = useFormik({
    initialValues: defaultValues,
    onSubmit: (val) => {
      //Intentional
    },
  })
  const [getClientFeatureflags, { data: featureFlags }] = useLazyQuery(GETCLIENTFEATUREFLAGS, {
    fetchPolicy: GLOBAL_CONSTANTS.networkOnly,
  })
  const [getCustomerInfoByTpvId] = useLazyQuery(
    GET_CUSTOMER_INFO_BY_TPV_ID,
    {
      fetchPolicy,
      onCompleted: (data: any) => {
        setCustomerData(data?.getCustomerInfoByTpvId)
        getClientFeatureflags({
          variables: {
            customerId: data?.getCustomerInfoByTpvId?.id,
          },
        })
      },
    }
  )

  const [getAgentInfoByTpvId] = useLazyQuery(GET_AGENT_INFO_BY_TPV_ID, {
    fetchPolicy,
    onCompleted: (data: any) => setAgentData(data?.getAgentInfoByTpvId),
  })

  const [getTpvRecording] = useLazyQuery(GET_TPV_RECORDING, {
    fetchPolicy,
    onCompleted: (data: any) => {
      const calls = data?.getTpvRecording.calls ?? []

      const tpvVerificationCalls = calls.filter((call: any) =>
        call.scriptPurpose === 'tpv_verification'
      );
      const welcomeScriptCalls = calls.filter((call: any) =>
        call.scriptPurpose === 'welcome_message'
      );


      const mergedCalls = [
        ...tpvVerificationCalls,
        ...welcomeScriptCalls,
      ]

      setRecordingData({
        ...data?.getTpvRecording,
        calls: mergedCalls,
      })
    },
  })

  useEffect(() => {
    if (tab === 3) {
      getTpvRecording({
        variables: {
          tpvId: tpvDetails?.tpvId,
        },
      })
    } else {
      formik.setFieldValue('qcStatus', tpvDetails?.qcStatus)
      getCustomerInfoByTpvId({
        variables: {
          tpvId: tpvDetails?.tpvId,
          enrollmentPosition: tpvDetails?.enrollmentPosition
        },
      })
      getAgentInfoByTpvId({
        variables: {
          tpvId: tpvDetails?.tpvId,
          enrollmentPosition: tpvDetails.enrollmentPosition

        },
      })
    }
  }, [tab])//eslint-disable-line react-hooks/exhaustive-deps

  const handleRecordTab = (targetTab: any) => {
    setRecordingTab(targetTab)
  }

  const handleSubmitted = () => {
    setCustomerData(null)
    getCustomerInfoByTpvId({
      variables: {
        tpvId: tpvDetails?.tpvId,
        enrollmentPosition: tpvDetails?.enrollmentPosition
      },
    })
  }


  const [getClientAllowedComponents] = useLazyQuery(GET_CLIENT_ALLOWED_COMPONENTS, {
    onCompleted: (data) => {
      setIsSolarProduct(data?.getClientAllowedComponents.includes('solarProductSetup'))
    },
    fetchPolicy: GLOBAL_CONSTANTS.networkOnly,
  })

  useEffect(() => {
    getClientAllowedComponents()
  }, [])// eslint-disable-line react-hooks/exhaustive-deps


  return (
    <div className='cust-det-block'>
      <div
        className='d-flex flex-row justify-content-between'
        style={{ height: '48px', marginBottom: '20px' }}
      >
        <div className='d-flex'>
          <div
            className='svg-icon svg-icon-3 svg-icon-muted me-4 mt-4 align-items-center'
            style={{ cursor: 'pointer' }}
            onClick={() => handleBackNavigation()}
          >
            <img src={backIcon} alt='back' />
          </div>
          <div className='d-flex flex-column'>
            <span className='fs-3 fw-bold text-gray-900'>Customer Details</span>
            <span className='text-gray-500 fw-semibold lh-20px fs-7'>
              Home - TPV Recording List - Customer
            </span>
          </div>
        </div>
      </div>
      <CustomerDetailsHeader
        tab={tab}
        setTab={setTab}
        formik={formik}
        tpvDetails={tpvDetails}
        featureFlags={featureFlags}
        customerData={customerData}
        isSolarProduct={isSolarProduct}
      />
      <div className='card mt-5'>
        <div className='card-body'>
          <div className='tab-content pt-3'>
            <div className={clsx('tab-pane', { active: tab === 0 })}>
              <CustomerInfo
                data={customerData}
                handleSubmitted={handleSubmitted}
                featureFlags={featureFlags}
                ediStatus={tpvDetails?.ediStatus}
                agentData={agentData}
                tpvId={tpvDetails?.tpvId}
                showAudit={getUrlParams.adminUser === 'systemadmin' ? true : false}
                showMapAndDistance={true}
              />
            </div>
            <div className={clsx('tab-pane', { active: tab === 1 })}>
              <ContactIntelligence data={customerData} />
            </div>
            <div className={clsx('tab-pane', { active: tab === 2 })}>
              <AgentInfo data={agentData} featureFlags={featureFlags} />
            </div>
            <div className={clsx('tab-pane', { active: tab === 3 })}>
              <RecordingInfo
                tpvId={tpvDetails?.tpvId}
                tpvStatus={tpvDetails.tpvStatus}
                data={recordingData}
                customerData={customerData}
                handleRecordTab={handleRecordTab}
              />
            </div>
            {(featureFlags?.getClientFeatureFlags?.enableEmailDocumentVerification ||
              featureFlags?.getClientFeatureFlags?.enableSmsDocumentVerification) && (
                <div className={clsx('tab-pane', { active: tab === 4 })}>
                  <ContractTPVReports tpvId={tpvDetails?.tpvId} enrollmentPosition={tpvDetails?.enrollmentPosition} />
                </div>
              )}
          </div>
        </div>
      </div>
      {tab === 3 && recordingData?.calls.length > 0 && (
        <div className='card mt-8'>
          <div className='card-body p-0'>
            <ScriptPromptComponent
              data={recordingData}
              recordingTab={recordingTab}
              customerData={customerData}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerDetails
